/**
 * BoardChrome (BL-62) — the ONE shared "board chrome" layer.
 *
 * The signed `docs/gameboard_mockup.html` reads as a single, premium, lit-CRT
 * frame because one chrome vocabulary runs across the whole board: a faint
 * scanline texture + the gold corner-etch L-ticks. In the live game that
 * coherence was missing — only the centre (`#game-wrapper::after`) had
 * scanlines, and each component injected its own textures ad-hoc. This module
 * is the single source (mirroring how `uiTokens` centralises material): ONE
 * injected stylesheet + a thin `applyBoardChrome(panel)` that drops a
 * non-interactive overlay (scanlines + the requested corner etch) onto a
 * column. No per-component duplication.
 *
 * Determinism-neutral (pure DOM/CSS, Layer 4). Cheap + reduced-motion-safe by
 * construction — the chrome is STATIC (no animation, no JS per frame), so it
 * adds no per-frame HUD cost and the BL-44 arena budget is untouched. Colour is
 * central (`uiTokens.UI_CHROME` → the mockup hues + the legendary gold etch) —
 * no new gameplay hex.
 */
import { UI_CHROME } from "./uiTokens.ts";

/** Corner ids — `t/b` (top/bottom) × `l/r` (left/right). */
export type BoardChromeCorner = "tl" | "tr" | "bl" | "br";

export interface BoardChromeOptions {
  /** Which corners get the premium gold etch (default: none — scanlines only). */
  corners?: BoardChromeCorner[];
  /** Drop the scanline layer (etch-only). Default: scanlines on. */
  scan?: boolean;
}

let stylesInjected = false;

/** Inject the single shared board-chrome stylesheet once. */
function ensureStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  if (document.getElementById("board-chrome-styles")) {
    stylesInjected = true;
    return;
  }
  stylesInjected = true;
  const etch = UI_CHROME.etchAccent;
  const style = document.createElement("style");
  style.id = "board-chrome-styles";
  style.textContent = `
    /* BL-62 — the ONE shared board chrome (scanlines + corner etch) applied
       across the columns so the whole frame reads as one picture. A single
       source; no per-component re-injection. Static ⇒ cheap + reduced-motion-
       safe; pointer-events:none ⇒ never intercepts input. */
    .board-chrome {
      position: absolute; inset: 0; z-index: 6;
      pointer-events: none; overflow: hidden;
    }
    .board-scan {
      position: absolute; inset: 0;
      mix-blend-mode: overlay; opacity: ${UI_CHROME.scanOpacity};
      background: repeating-linear-gradient(
        0deg, rgba(255, 255, 255, 0.05) 0 1px, transparent 1px 3px);
    }
    .board-etch {
      position: absolute; width: 22px; height: 22px; opacity: 0.65;
    }
    .board-etch::before, .board-etch::after {
      content: ""; position: absolute; background: ${etch};
      box-shadow: 0 0 5px color-mix(in srgb, ${etch} 55%, transparent);
    }
    .board-etch.t { top: 5px } .board-etch.b { bottom: 5px }
    .board-etch.l { left: 5px } .board-etch.r { right: 5px }
    .board-etch.t::before { top: 0; width: 13px; height: 1.5px }
    .board-etch.t::after { top: 0; width: 1.5px; height: 9px }
    .board-etch.b::before { bottom: 0; width: 13px; height: 1.5px }
    .board-etch.b::after { bottom: 0; width: 1.5px; height: 9px }
    .board-etch.l::before, .board-etch.l::after { left: 0 }
    .board-etch.r::before, .board-etch.r::after { right: 0 }
  `;
  document.head.appendChild(style);
}

/**
 * Apply the shared board chrome to a column/panel. Appends a single
 * non-interactive overlay (scanlines + the requested corner etch). The host
 * should establish a positioning context (the side panels / status-row are
 * `position:relative`). Returns a cleanup fn that removes the overlay.
 */
export function applyBoardChrome(
  panel: HTMLElement,
  opts: BoardChromeOptions = {},
): () => void {
  ensureStyles();
  if (typeof document === "undefined") return () => {};

  const chrome = document.createElement("div");
  chrome.className = "board-chrome";

  if (opts.scan !== false) {
    const scan = document.createElement("div");
    scan.className = "board-scan";
    chrome.appendChild(scan);
  }

  for (const c of opts.corners ?? []) {
    const e = document.createElement("span");
    const vy = c[0] === "t" ? "t" : "b";
    const hx = c[1] === "l" ? "l" : "r";
    e.className = `board-etch ${vy} ${hx}`;
    chrome.appendChild(e);
  }

  panel.appendChild(chrome);
  return () => chrome.remove();
}

/** Test/reset hook — drops the injected-style guard. */
export function __resetBoardChromeForTest(): void {
  stylesInjected = false;
}
